import { useState } from "react";
import { Handle, Position, type NodeProps } from "@xyflow/react";
import { Button, Dropdown, Tag, Tooltip, Typography, theme } from "antd";
import {
  AuditOutlined,
  BranchesOutlined,
  ClockCircleOutlined,
  DeleteOutlined,
  EditOutlined,
  ExclamationCircleFilled,
  FileTextOutlined,
  HolderOutlined,
  MoreOutlined,
  PlusOutlined,
  WarningFilled,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useCanvas } from "./CanvasContext";
import { problemsFor, worstOf } from "../validation";
import { describeCondition, isConditional } from "../conditionText";
import { CHIP_HEIGHT, NODE_WIDTH } from "./layout";
import type { StageDraft } from "../types";

const { Text } = Typography;

export type StageNodeData = {
  stage: StageDraft;
  index: number;
  total: number;
};

/**
 * One section or sign-off, listed inside the step that holds it.
 *
 * Draggable with the browser's own drag-and-drop rather than dnd-kit, because
 * React Flow owns pointer events on a node -- it uses them for panning and for
 * dragging the node itself, so a pointer-sensor library fights it for every
 * gesture. Native drag events are a separate channel that React Flow does not
 * listen to, so the two coexist without either having to be disabled.
 */
const ItemChip = ({
  label,
  icon,
  selected,
  problems,
  onSelect,
  onDragStart,
  onDragEnd,
  extra,
}: {
  label: string;
  icon: React.ReactNode;
  selected: boolean;
  problems: number;
  onSelect: () => void;
  onDragStart: () => void;
  onDragEnd: () => void;
  extra?: React.ReactNode;
}) => {
  const { token } = theme.useToken();
  return (
    <div
      draggable
      onDragStart={(e) => {
        // Text/plain keeps Firefox willing to start the drag at all.
        e.dataTransfer.setData("text/plain", label);
        e.dataTransfer.effectAllowed = "move";
        e.stopPropagation();
        onDragStart();
      }}
      onDragEnd={onDragEnd}
      onClick={(e) => {
        e.stopPropagation();
        onSelect();
      }}
      className="nodrag"
      style={{
        display: "flex",
        alignItems: "center",
        gap: 6,
        height: CHIP_HEIGHT - 6,
        padding: "0 8px",
        marginBottom: 6,
        borderRadius: token.borderRadius,
        cursor: "grab",
        background: selected ? token.controlItemBgActive : token.colorFillQuaternary,
        border: `1px solid ${
          selected ? token.colorPrimaryBorder : "transparent"
        }`,
        fontSize: 12,
      }}
    >
      <span style={{ color: token.colorTextTertiary, display: "flex" }}>
        {icon}
      </span>
      <span
        style={{
          flex: 1,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {label}
      </span>
      {extra}
      {problems > 0 && (
        <ExclamationCircleFilled
          style={{ color: token.colorError, fontSize: 11 }}
        />
      )}
    </div>
  );
};

/**
 * A step, as a card on the canvas.
 *
 * The card carries what is *inside* the step, not just its name. That is the
 * whole reason the canvas can replace the outline as the primary surface: the
 * old diagram said "3 sections" and so could only ever be a picture of the
 * process to look at, while the real work happened in a list somewhere else.
 * Listing them makes the canvas the place the work happens.
 */
const StageNode = ({ data, selected }: NodeProps) => {
  const d = data as unknown as StageNodeData;
  const { stage, index } = d;
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const canvas = useCanvas();
  const [over, setOver] = useState(false);

  const isEntry = stage.kind === "DataEntry";
  const accent = isEntry ? token.colorPrimary : token.purple6;
  const problems = problemsFor(canvas.problems, { type: "stage", key: stage.key });
  const worst = worstOf(problems);
  const applies = canvas.simulation ? canvas.simulation[stage.key] ?? true : null;
  const skipped = applies === false;
  const conditional = isConditional(stage.condition);

  // A step only accepts what it is for: sections go in steps people fill in,
  // sign-offs in steps people sign. The server refuses the other way round, so
  // the card refuses to light up rather than accepting a drop that will fail.
  const accepts =
    canvas.dragging &&
    ((isEntry && canvas.dragging.kind === "form") ||
      (!isEntry && canvas.dragging.kind === "workflow"));

  const items = isEntry
    ? stage.form_ids.map((id) => ({
        id,
        kind: "form" as const,
        label: canvas.sections[id]?.title ?? `#${id}`,
        selected:
          canvas.selection?.type === "section" && canvas.selection.id === id,
        problems: problemsFor(canvas.problems, { type: "section", id }).length,
        extra: canvas.sections[id]?.is_required ? null : (
          <Tag style={{ marginInlineEnd: 0, fontSize: 10, lineHeight: "14px" }}>
            {t("Optional")}
          </Tag>
        ),
      }))
    : stage.workflow_ids.map((id) => {
        const workflow = canvas.workflows.find((w) => w.id === id);
        const slots = workflow?.roles?.length ?? 0;
        return {
          id,
          kind: "workflow" as const,
          label: workflow?.name?.trim() || t("Untitled sign-off"),
          selected:
            canvas.selection?.type === "workflow" && canvas.selection.id === id,
          problems: problemsFor(canvas.problems, { type: "workflow", id }).length,
          extra: slots > 0 && (
            <Tag style={{ marginInlineEnd: 0, fontSize: 10, lineHeight: "14px" }}>
              {slots === 1 ? t("1 slot") : t("{{count}} slots", { count: slots })}
            </Tag>
          ),
        };
      });

  const drop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setOver(false);
    if (!canvas.dragging || !accepts) return;
    canvas.onPlaceItem(canvas.dragging.kind, canvas.dragging.id, stage.key, -1);
    canvas.setDragging(null);
  };

  return (
    <div
      onDragOver={(e) => {
        if (!accepts) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";
        setOver(true);
      }}
      onDragLeave={() => setOver(false)}
      onDrop={drop}
      style={{
        width: NODE_WIDTH,
        borderRadius: token.borderRadiusLG,
        background: token.colorBgContainer,
        border: `2px solid ${
          over && accepts
            ? token.colorSuccess
            : selected
            ? accent
            : token.colorBorderSecondary
        }`,
        boxShadow: selected
          ? `0 0 0 4px ${accent}22`
          : token.boxShadowTertiary,
        opacity: skipped ? 0.5 : 1,
        overflow: "hidden",
      }}
    >
      <Handle type="target" position={Position.Top} style={{ opacity: 0 }} />

      {/* Header: the drag handle for reordering, the number, the name. */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          padding: "8px 10px",
          borderBottom: `1px solid ${token.colorBorderSecondary}`,
          borderLeft: `3px solid ${accent}`,
        }}
      >
        <HolderOutlined
          style={{ color: token.colorTextQuaternary, cursor: "grab" }}
        />
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: 20,
            height: 20,
            flex: "0 0 auto",
            borderRadius: "50%",
            background: accent,
            color: "#fff",
            fontSize: 11,
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {index + 1}
        </span>
        <Text
          strong
          ellipsis
          type={stage.name.trim() ? undefined : "danger"}
          style={{
            flex: 1,
            fontSize: 13,
            textDecoration: skipped ? "line-through" : undefined,
          }}
        >
          {stage.name.trim() || t("Name this step")}
        </Text>

        {/* A deadline set in the inspector and invisible on the canvas is a
            setting nobody sees. Small and quiet: it is a property of the step,
            not a warning about it. */}
        {stage.sla_hours ? (
          <Tooltip
            title={t(
              "Work in this step is due {{count}} hours after it is asked for.",
              { count: stage.sla_hours }
            )}
          >
            <Tag
              icon={<ClockCircleOutlined />}
              style={{ marginInlineEnd: 0, fontSize: 10, lineHeight: "14px" }}
            >
              {t("{{count}}h", { count: stage.sla_hours })}
            </Tag>
          </Tooltip>
        ) : null}

        {worst && (
          <Tooltip
            title={problems.map((p) => t(p.message, p.params)).join("\n")}
          >
            {worst === "error" ? (
              <ExclamationCircleFilled style={{ color: token.colorError }} />
            ) : (
              <WarningFilled style={{ color: token.colorWarning }} />
            )}
          </Tooltip>
        )}

        <Dropdown
          trigger={["click"]}
          menu={{
            items: [
              {
                key: "edit",
                icon: <EditOutlined />,
                label: t("Edit this step"),
                onClick: () => canvas.onSelect({ type: "stage", key: stage.key }),
              },
              isEntry
                ? {
                    key: "section",
                    icon: <PlusOutlined />,
                    label: t("Add a section"),
                    onClick: () => canvas.onCreateSection(stage.key),
                  }
                : {
                    key: "signoff",
                    icon: <PlusOutlined />,
                    label: t("Add a sign-off"),
                    onClick: () => canvas.onAddWorkflow(stage.key),
                  },
              { type: "divider" as const },
              {
                key: "remove",
                icon: <DeleteOutlined />,
                danger: true,
                label: t("Remove this step"),
                onClick: () => canvas.onRemoveStage(stage.key),
              },
            ],
          }}
        >
          <Tooltip title={t("More — rename, add, remove")}>
            <Button
              className="nodrag"
              type="text"
              size="small"
              icon={<MoreOutlined />}
              aria-label={t("More — rename, add, remove")}
              onClick={(e) => e.stopPropagation()}
              style={{ marginRight: -4 }}
            />
          </Tooltip>
        </Dropdown>
      </div>

      {/* Contents. */}
      <div style={{ padding: "8px 10px 2px" }}>
        {items.length === 0 ? (
          <div
            style={{
              height: CHIP_HEIGHT - 6,
              display: "flex",
              alignItems: "center",
              gap: 6,
              marginBottom: 6,
              padding: "0 8px",
              borderRadius: token.borderRadius,
              border: `1px dashed ${token.colorBorder}`,
              color: token.colorTextTertiary,
              fontSize: 12,
            }}
          >
            {isEntry ? <FileTextOutlined /> : <AuditOutlined />}
            {isEntry
              ? t("No sections yet — drop one here")
              : t("No sign-offs yet — drop one here")}
          </div>
        ) : (
          items.map((item) => (
            <ItemChip
              key={`${item.kind}-${item.id}`}
              label={item.label}
              icon={isEntry ? <FileTextOutlined /> : <AuditOutlined />}
              selected={item.selected}
              problems={item.problems}
              extra={item.extra}
              onSelect={() =>
                canvas.onSelect(
                  item.kind === "form"
                    ? { type: "section", id: item.id }
                    : { type: "workflow", id: item.id }
                )
              }
              onDragStart={() =>
                canvas.setDragging({ kind: item.kind, id: item.id })
              }
              onDragEnd={() => canvas.setDragging(null)}
            />
          ))
        )}
      </div>

      {/* The rule, along the bottom, where a conditional step wears it. */}
      {conditional && (
        <Tooltip title={describeCondition(stage.condition, canvas.fields, t as any)}>
          <div
            className="nodrag"
            onClick={(e) => {
              e.stopPropagation();
              canvas.onSelect({ type: "stage", key: stage.key });
            }}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 5,
              padding: "5px 10px",
              cursor: "pointer",
              background: token.colorWarningBg,
              borderTop: `1px solid ${token.colorWarningBorder}`,
              color: token.colorWarningText,
              fontSize: 11,
            }}
          >
            <BranchesOutlined />
            <span
              style={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {t("Only when")}{" "}
              {describeCondition(stage.condition, canvas.fields, t as any)}
            </span>
          </div>
        </Tooltip>
      )}

      {skipped && (
        <div
          style={{
            padding: "3px 10px",
            background: token.colorFillQuaternary,
            fontSize: 11,
            color: token.colorTextTertiary,
          }}
        >
          {t("Skipped for these answers")}
        </div>
      )}

      <Handle type="source" position={Position.Bottom} style={{ opacity: 0 }} />
    </div>
  );
};

export default StageNode;
